import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PublicApiError, publicPost } from '../lib/publicFetch';

const PLANS = [
  { id: '', label: 'Not sure yet' },
  { id: 'solo', label: 'Solo' },
  { id: 'pro', label: 'Pro' },
  { id: 'enterprise', label: 'Enterprise' },
] as const;

export function DemoForm() {
  const [params] = useSearchParams();
  const initialPlan = params.get('plan') ?? '';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [club, setClub] = useState('');
  const [plan, setPlan] = useState(PLANS.some((item) => item.id === initialPlan) ? initialPlan : '');
  const [message, setMessage] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [status, setStatus] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setStatus('busy');
    setError('');
    try {
      await publicPost('/public/demo', {
        name,
        email,
        club,
        plan,
        message,
        company_url: companyUrl,
      });
      setStatus('done');
    } catch (err) {
      const messageText =
        err instanceof PublicApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'We could not send that just now. Please try again later.';
      setError(messageText);
      setStatus('error');
    }
  }

  if (status === 'done') {
    return (
      <div role="status">
        <h2 className="text-mkt-section-sm">Thanks. We have your request.</h2>
        <p className="mt-4 text-mkt-body text-mkt-secondary">
          We will reply to {email.trim()} about a demo.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <label className="block text-mkt-body">
        <span>Name</span>
        <input
          name="name"
          required
          autoComplete="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="mt-2 h-11 w-full rounded-xl border border-mkt-line bg-white px-3"
          disabled={status === 'busy'}
        />
      </label>
      <label className="block text-mkt-body">
        <span>Email</span>
        <input
          name="email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="mt-2 h-11 w-full rounded-xl border border-mkt-line bg-white px-3"
          disabled={status === 'busy'}
        />
      </label>
      <label className="block text-mkt-body">
        <span>Club</span>
        <input
          name="club"
          autoComplete="organization"
          value={club}
          onChange={(event) => setClub(event.target.value)}
          className="mt-2 h-11 w-full rounded-xl border border-mkt-line bg-white px-3"
          disabled={status === 'busy'}
        />
      </label>
      <label className="block text-mkt-body">
        <span>Plan</span>
        <select
          name="plan"
          value={plan}
          onChange={(event) => setPlan(event.target.value)}
          className="mt-2 h-11 w-full rounded-xl border border-mkt-line bg-white px-3"
          disabled={status === 'busy'}
        >
          {PLANS.map((item) => (
            <option key={item.id || 'none'} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-mkt-body">
        <span>What do you want to see?</span>
        <textarea
          name="message"
          rows={4}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          className="mt-2 w-full rounded-xl border border-mkt-line bg-white px-3 py-3"
          disabled={status === 'busy'}
        />
      </label>
      <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden>
        <label>
          Company website
          <input
            tabIndex={-1}
            autoComplete="off"
            value={companyUrl}
            onChange={(event) => setCompanyUrl(event.target.value)}
          />
        </label>
      </div>
      {error ? (
        <p className="rounded-xl bg-red-50 px-4 py-3 text-mkt-body text-red-900" role="alert">
          {error}
        </p>
      ) : null}
      <button
        type="submit"
        className="inline-flex h-11 items-center rounded-full bg-mkt-accent px-6 text-mkt-body text-white hover:bg-mkt-accent-hover disabled:opacity-60"
        disabled={status === 'busy'}
      >
        {status === 'busy' ? 'Sending…' : 'Book a demo'}
      </button>
    </form>
  );
}
